<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import inboxesAPI from 'dashboard/api/inboxes';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { CARD_CLASS, SELECT_CLASS } from '../constants';

// The development build adds a fake WhatsApp inbox with this id to the list.
// Saving it would make every WhatsApp row fail, so it is never offered.
const DEV_MOCK_INBOX_ID = 999999;

// Which Chatwoot inbox sends the module's WhatsApp messages, kept in the
// settings resource as `outbound_inbox_id` (null = the first inbox that is in
// co-living mode). The server does not check the inbox, so the card warns when
// the choice cannot send: no bot connected to it, or not a WhatsApp inbox.
export default {
  name: 'OutboundInboxCard',
  data() {
    return {
      cardClass: CARD_CLASS,
      selectClass: SELECT_CLASS,
      inboxId: '',
      isSaving: false,
      hasBot: null,
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    ...mapGetters({ allInboxes: 'inboxes/getInboxes' }),
    inboxes() {
      return (this.allInboxes || []).filter(
        inbox => inbox.id !== DEV_MOCK_INBOX_ID
      );
    },
    // A saved id the inbox list no longer has (deleted inbox).
    isMissing() {
      return (
        this.inboxId !== '' &&
        !this.inboxes.some(inbox => inbox.id === this.inboxId)
      );
    },
    selected() {
      return this.inboxes.find(inbox => inbox.id === this.inboxId) || null;
    },
    isWhatsApp() {
      return (
        !!this.selected && this.selected.channel_type === 'Channel::Whatsapp'
      );
    },
    isChanged() {
      const saved = this.settings ? this.settings.outbound_inbox_id : null;
      return (saved || '') !== this.inboxId;
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(settings) {
        if (settings) this.inboxId = settings.outbound_inbox_id || '';
      },
    },
    inboxId: {
      immediate: true,
      handler() {
        this.checkBot();
      },
    },
  },
  methods: {
    ...mapActions('coLivingConfig', ['saveSettings']),
    // Messages are posted with the inbox's agent bot token; without a bot every
    // WhatsApp row fails with `no_agent_bot`.
    async checkBot() {
      this.hasBot = null;
      const id = this.inboxId;
      if (id === '') return;
      try {
        const { data } = await inboxesAPI.getAgentBot(id);
        if (id === this.inboxId) this.hasBot = !!(data && data.id);
      } catch (e) {
        // unknown: say nothing rather than a wrong warning
      }
    },
    async onSave() {
      this.isSaving = true;
      try {
        await this.saveSettings({ outbound_inbox_id: this.inboxId || null });
        useAlert(this.$t('CO_LIVING.COMMON.SAVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.SETTINGS.SAVE_FAILED'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <section :class="cardClass" data-testid="co-living-outbound-inbox">
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
      {{ $t('CO_LIVING.ROUTING.OUTBOUND.TITLE') }}
    </h2>
    <p class="mt-1 mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('CO_LIVING.ROUTING.OUTBOUND.DESCRIPTION') }}
    </p>
    <form class="grid grid-cols-1 gap-4" @submit.prevent="onSave">
      <div>
        <label
          for="co-living-outbound-inbox-select"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.ROUTING.OUTBOUND.LABEL') }}
        </label>
        <select
          id="co-living-outbound-inbox-select"
          v-model="inboxId"
          :class="selectClass"
          data-testid="co-living-outbound-inbox-select"
        >
          <option value="">
            {{ $t('CO_LIVING.ROUTING.OUTBOUND.DEFAULT') }}
          </option>
          <option v-if="isMissing" :value="inboxId">
            {{ $t('CO_LIVING.ROUTING.OUTBOUND.MISSING', { id: inboxId }) }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <p
          v-if="isMissing"
          class="mt-1 text-xs text-red-600 dark:text-red-400"
          data-testid="co-living-outbound-inbox-missing"
        >
          {{ $t('CO_LIVING.ROUTING.OUTBOUND.MISSING_HINT') }}
        </p>
        <p
          v-if="hasBot === false"
          class="mt-1 text-xs text-amber-700 dark:text-amber-300"
          data-testid="co-living-outbound-inbox-no-bot"
        >
          {{ $t('CO_LIVING.ROUTING.OUTBOUND.NO_BOT') }}
        </p>
        <p
          v-if="selected && !isWhatsApp"
          class="mt-1 text-xs text-amber-700 dark:text-amber-300"
          data-testid="co-living-outbound-inbox-not-whatsapp"
        >
          {{ $t('CO_LIVING.ROUTING.OUTBOUND.NOT_WHATSAPP') }}
        </p>
      </div>
      <div>
        <woot-button
          type="submit"
          :is-loading="isSaving"
          :is-disabled="!isChanged"
          data-testid="co-living-outbound-inbox-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </section>
</template>
